import {
  FILE_NAMES,
  RANK_NAMES,
  WHITE,
  fileOf,
  isLightSquare,
  square as makeSquare,
  rankOf,
  squareName,
  type Color,
  type Move,
  type MoveIntent,
  type Square,
} from '@chess/core'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type PointerEvent,
} from 'react'
import { coordsOf, squaresInViewOrder, type PlacedPiece, type ViewCoords } from './layout.js'
import { PieceGlyph } from './pieces.js'

/** How long a piece takes to travel, and the curve it travels on. */
const MOVE_MS = 170
const MOVE_EASING = 'cubic-bezier(0.22, 0.66, 0.36, 1)'
/** A captured piece fades under the arriving one rather than blinking out. */
const CAPTURE_MS = 150
const SNAP_BACK_MS = 150

/**
 * Pointer travel before a press counts as a drag. A mouse is precise; a finger
 * on a moving train is not, and at the mouse threshold every tap registers as a
 * one-pixel drag.
 */
const DRAG_THRESHOLD_PX = { mouse: 4, pen: 5, touch: 10 } as const

/**
 * How far above the finger the dragged piece rides, in squares, and how much it
 * grows. Without this your own hand covers the piece and the square under it —
 * the single worst thing about playing chess in a browser on a phone.
 */
const TOUCH_LIFT_SQUARES = 0.85
const TOUCH_DRAG_SCALE = 1.35

export interface BoardProps {
  readonly layout: readonly PlacedPiece[]
  readonly orientation: Color
  /** Moves playable right now. Empty when it is not the viewer's turn. */
  readonly legalMoves: readonly Move[]
  /** Moves that may be queued for after the opponent replies. */
  readonly premoveMoves: readonly Move[]
  readonly lastMove: Move | null
  readonly checkSquare: Square | null
  readonly premove: MoveIntent | null
  readonly onMove: (intent: MoveIntent) => void
  readonly onPremove: (intent: MoveIntent | null) => void
  /** Fired instead of `onMove` when the destination needs a piece chosen. */
  readonly onPromotion: (from: Square, to: Square, queued: boolean) => void
  readonly animate: boolean
  /** Half-moves shown. Changing it clears any in-progress selection. */
  readonly ply: number
  /** Fired when a move is refused, so the parent can react. */
  readonly onIllegal?: () => void
  /** Fired when a piece is picked up, for haptics. */
  readonly onPickUp?: () => void
}

interface DragState {
  readonly pointerId: number
  readonly from: Square
  readonly touch: boolean
  /** Board-relative position in square units; fractional while dragging. */
  readonly x: number
  readonly y: number
  readonly moved: boolean
}

const squareFromPoint = (
  clientX: number,
  clientY: number,
  rect: DOMRect,
  orientation: Color,
): Square | null => {
  const column = Math.floor(((clientX - rect.left) / rect.width) * 8)
  const row = Math.floor(((clientY - rect.top) / rect.height) * 8)
  if (column < 0 || column > 7 || row < 0 || row > 7) return null
  return orientation === WHITE ? makeSquare(column, 7 - row) : makeSquare(7 - column, row)
}

const transformFor = ({ x, y }: ViewCoords): string => `translate(${x * 100}%, ${y * 100}%)`

const BoardImpl = ({
  layout,
  orientation,
  legalMoves,
  premoveMoves,
  lastMove,
  checkSquare,
  premove,
  onMove,
  onPremove,
  onPromotion,
  animate,
  ply,
  onIllegal,
  onPickUp,
}: BoardProps): JSX.Element => {
  const boardRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState<Square | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [refused, setRefused] = useState<Square | null>(null)
  const [refuseNonce, setRefuseNonce] = useState(0)
  /** Pieces that left the board this render, kept briefly so they can fade. */
  const [ghosts, setGhosts] = useState<readonly PlacedPiece[]>([])

  const refuse = useCallback(
    (square: Square): void => {
      setRefused(square)
      setRefuseNonce((value) => value + 1)
      onIllegal?.()
    },
    [onIllegal],
  )

  useEffect(() => {
    if (refused === null) return
    const id = window.setTimeout(() => setRefused(null), 360)
    return () => window.clearTimeout(id)
  }, [refused, refuseNonce])

  // While it is the opponent's turn, the same gestures queue a premove.
  const queueing = legalMoves.length === 0 && premoveMoves.length > 0
  const usable = queueing ? premoveMoves : legalMoves

  const byOrigin = useMemo(() => {
    const map = new Map<Square, Move[]>()
    for (const move of usable) {
      const existing = map.get(move.from)
      if (existing === undefined) map.set(move.from, [move])
      else existing.push(move)
    }
    return map
  }, [usable])

  const destinations = useMemo(
    () => new Map((byOrigin.get(selected as Square) ?? []).map((move) => [move.to, move])),
    [byOrigin, selected],
  )

  useEffect(() => setSelected(null), [ply])

  /* ── motion ───────────────────────────────────────────────────────────── */

  const nodes = useRef(new Map<string, HTMLDivElement>())
  const placed = useRef(new Map<string, ViewCoords>())
  const previousLayout = useRef<readonly PlacedPiece[]>(layout)

  /**
   * Animate pieces from where they were to where they now are.
   *
   * Explicit rather than a CSS transition: a transition only fires if the same
   * DOM node happens to receive a new transform in a later paint, so any
   * remount — a reconnect snapshot, entering review, a rematch — silently
   * skipped the animation. That intermittency was the whole "it only works
   * sometimes" complaint. Driving it here means it plays because we played it.
   */
  useLayoutEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    for (const piece of layout) {
      const to = coordsOf(piece.square, orientation)
      const from = placed.current.get(piece.id)
      placed.current.set(piece.id, to)

      if (!animate || reduced || from === undefined) continue
      if (from.x === to.x && from.y === to.y) continue
      // The piece under the finger is already where the finger put it.
      if (drag?.from === piece.square) continue

      nodes.current
        .get(piece.id)
        ?.animate([{ transform: transformFor(from) }, { transform: transformFor(to) }], {
          duration: MOVE_MS,
          easing: MOVE_EASING,
        })
    }

    for (const id of [...placed.current.keys()]) {
      if (!layout.some((piece) => piece.id === id)) placed.current.delete(id)
    }
    // `drag` is deliberately not a dependency: this must run on layout changes,
    // not on every pointer move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, orientation, animate])

  /** Captured pieces linger for a moment so the capture reads as a capture. */
  useLayoutEffect(() => {
    const present = new Set(layout.map((piece) => piece.id))
    const departed = previousLayout.current.filter((piece) => !present.has(piece.id))
    previousLayout.current = layout

    if (!animate || departed.length === 0) return
    setGhosts(departed)
    const id = window.setTimeout(() => setGhosts([]), CAPTURE_MS)
    return () => window.clearTimeout(id)
  }, [layout, animate])

  /** Slide a dropped piece home instead of teleporting it. */
  const snapBack = useCallback(
    (state: DragState): void => {
      const piece = layout.find((candidate) => candidate.square === state.from)
      const node = piece === undefined ? undefined : nodes.current.get(piece.id)
      if (node === undefined) return

      const home = coordsOf(state.from, orientation)
      node.animate(
        [
          { transform: transformFor({ x: state.x, y: state.y }) },
          { transform: transformFor(home) },
        ],
        { duration: SNAP_BACK_MS, easing: 'ease-out' },
      )
    },
    [layout, orientation],
  )

  /* ── input ────────────────────────────────────────────────────────────── */

  const commit = useCallback(
    (from: Square, to: Square): void => {
      const candidates = (byOrigin.get(from) ?? []).filter((move) => move.to === to)
      if (candidates.length === 0) return
      if (candidates.some((move) => move.kind === 'promotion')) {
        onPromotion(from, to, queueing)
      } else if (queueing) {
        onPremove({ from, to })
      } else {
        onMove({ from, to })
      }
      setSelected(null)
    },
    [byOrigin, onMove, onPremove, onPromotion, queueing],
  )

  const pointFor = (event: PointerEvent<HTMLDivElement>, rect: DOMRect): ViewCoords => ({
    x: ((event.clientX - rect.left) / rect.width) * 8 - 0.5,
    y: ((event.clientY - rect.top) / rect.height) * 8 - 0.5,
  })

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (event.button === 2) {
        setSelected(null)
        onPremove(null)
        return
      }
      const rect = boardRef.current?.getBoundingClientRect()
      if (rect === undefined) return
      const target = squareFromPoint(event.clientX, event.clientY, rect, orientation)
      if (target === null) return

      if (selected !== null && destinations.has(target)) {
        commit(selected, target)
        return
      }

      if (!byOrigin.has(target)) {
        // Tapping a square this piece cannot reach is an attempted move, not a
        // change of mind — say no rather than silently dropping the selection.
        if (selected !== null) refuse(selected)
        setSelected(null)
        return
      }

      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        /* Drag still works; it just ends if the pointer leaves the element. */
      }

      const point = pointFor(event, rect)
      setSelected(target)
      onPickUp?.()
      setDrag({
        pointerId: event.pointerId,
        from: target,
        touch: event.pointerType === 'touch',
        x: point.x,
        y: point.y,
        moved: false,
      })
    },
    [byOrigin, commit, destinations, onPickUp, onPremove, orientation, refuse, selected],
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (drag === null || drag.pointerId !== event.pointerId) return
      const rect = boardRef.current?.getBoundingClientRect()
      if (rect === undefined) return

      const point = pointFor(event, rect)
      const threshold =
        DRAG_THRESHOLD_PX[(event.pointerType as keyof typeof DRAG_THRESHOLD_PX) ?? 'mouse'] ??
        DRAG_THRESHOLD_PX.mouse
      const travelled = Math.hypot(point.x - drag.x, point.y - drag.y) * (rect.width / 8)

      setDrag({ ...drag, x: point.x, y: point.y, moved: drag.moved || travelled > threshold })
    },
    [drag],
  )

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      if (drag === null || drag.pointerId !== event.pointerId) return
      const rect = boardRef.current?.getBoundingClientRect()
      setDrag(null)
      if (rect === undefined) return

      // A tap leaves the piece selected so the next tap can be the destination.
      if (!drag.moved) return

      const target = squareFromPoint(event.clientX, event.clientY, rect, orientation)
      if (target === null || target === drag.from) {
        snapBack(drag)
        return
      }
      if (!(byOrigin.get(drag.from) ?? []).some((move) => move.to === target)) {
        snapBack(drag)
        refuse(drag.from)
        return
      }
      commit(drag.from, target)
    },
    [byOrigin, commit, drag, orientation, refuse, snapBack],
  )

  /** The square a dragged piece would land on, for the drop ring. */
  const hovered = useMemo<Square | null>(() => {
    if (drag === null || !drag.moved) return null
    const file = Math.round(drag.x)
    const rank = Math.round(drag.y)
    if (file < 0 || file > 7 || rank < 0 || rank > 7) return null
    return orientation === WHITE ? makeSquare(file, 7 - rank) : makeSquare(7 - file, rank)
  }, [drag, orientation])

  const lastFrom = lastMove?.from ?? null
  const lastTo = lastMove?.to ?? null

  return (
    <div
      ref={boardRef}
      className="board-surface relative aspect-square w-full overflow-hidden rounded"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onContextMenu={(event) => event.preventDefault()}
      role="grid"
      aria-label="Chess board"
    >
      <div className="absolute inset-0 grid grid-cols-8 grid-rows-8">
        {squaresInViewOrder(orientation).map((square) => {
          const light = isLightSquare(square)
          const destination = destinations.get(square)
          const occupied = layout.some((piece) => piece.square === square)

          const highlight =
            square === checkSquare
              ? `square-check${refused !== null ? ' square-refuse' : ''}`
              : square === premove?.from || square === premove?.to
                ? 'square-premove'
                : square === selected
                  ? 'square-selected'
                  : square === lastFrom || square === lastTo
                    ? 'square-last-move square-fade-in'
                    : ''

          const hint =
            destination === undefined
              ? ''
              : occupied || destination.kind === 'enPassant'
                ? 'hint-capture'
                : 'hint-move'

          return (
            <div
              key={square}
              data-square={squareName(square)}
              role="gridcell"
              aria-label={squareName(square)}
              className={`relative ${light ? 'bg-board-light' : 'bg-board-dark'} ${highlight} ${hint} ${
                square === hovered ? 'square-hover' : ''
              }`}
            >
              <Coordinates square={square} orientation={orientation} light={light} />
            </div>
          )
        })}
      </div>

      {ghosts.map((piece) => (
        <div
          key={`ghost-${piece.id}`}
          className="piece piece-vanish"
          style={{ transform: transformFor(coordsOf(piece.square, orientation)) }}
        >
          <PieceGlyph color={piece.color} type={piece.type} className="h-full w-full" />
        </div>
      ))}

      {layout.map((piece) => {
        const held = drag !== null && drag.moved && drag.from === piece.square ? drag : null
        const home = coordsOf(piece.square, orientation)
        const lift = held !== null && held.touch ? TOUCH_LIFT_SQUARES : 0
        const at = held === null ? home : { x: held.x, y: held.y - lift }
        const scale = held !== null && held.touch ? TOUCH_DRAG_SCALE : 1
        const shaking = refused === piece.square

        return (
          <div
            key={piece.id}
            ref={(node) => {
              if (node === null) nodes.current.delete(piece.id)
              else nodes.current.set(piece.id, node)
            }}
            className={`piece ${held !== null ? 'piece-dragging' : ''}`}
            style={{ transform: transformFor(at) }}
          >
            {/* Inner wrappers so the shake, the settle and the drag scale never
                overwrite the position transform that `.piece` owns. */}
            <div
              key={shaking ? refuseNonce : 'still'}
              className={`h-full w-full ${shaking ? 'piece-refuse' : ''}`}
              style={scale === 1 ? undefined : { transform: `scale(${scale})` }}
            >
              <PieceGlyph color={piece.color} type={piece.type} className="h-full w-full" />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const Board = memo(BoardImpl)

/**
 * File letters along the bottom edge and rank numbers along the left, tucked
 * into the corner of the square and tinted to contrast with it.
 */
const Coordinates = ({
  square,
  orientation,
  light,
}: {
  readonly square: Square
  readonly orientation: Color
  readonly light: boolean
}): JSX.Element | null => {
  const file = fileOf(square)
  const rank = rankOf(square)
  const onLeftEdge = orientation === WHITE ? file === 0 : file === 7
  const onBottomEdge = orientation === WHITE ? rank === 0 : rank === 7
  if (!onLeftEdge && !onBottomEdge) return null

  const tint = light ? 'text-coord-on-light' : 'text-coord-on-dark'
  return (
    <>
      {onLeftEdge && (
        <span className={`absolute top-0.5 left-1 text-[11px] font-bold ${tint} sm:text-xs`}>
          {RANK_NAMES[rank]}
        </span>
      )}
      {onBottomEdge && (
        <span className={`absolute right-1 bottom-0.5 text-[11px] font-bold ${tint} sm:text-xs`}>
          {FILE_NAMES[file]}
        </span>
      )}
    </>
  )
}
